import { CallMetricFilters } from '../call-metric-filters/call-metric-filters';
import { CallMetricHeader } from '../call-metric-header/call-metric-header';
import styles from './conversations-header.module.scss';
import RefreshArrow from '@/assets/icons/refresh-ccw-01.svg?react';
import Search from '@/assets/icons/search-md.svg?react';
import Download1 from '@/assets/icons/download-04.svg?react';
import Glass from '@/assets/icons/magnifying-glass.svg?react';
import { Card, Flex, Input, Spin, Tooltip } from 'antd';
import { Button } from '@/components/button/button';
import TopicChip from '../topic-chip/topic-chip';
import { useMemo, useState } from 'react';
import { debounce } from '@/utils/debounce/debounce';
import Dropdown from '../form/dropdown/dropdown';
import { CallLogFilters } from '@/services/call-log-service/call-log-service.types';
import {
  AGENT_OPTIONS,
  CONVERSATION_TYPE_OPTIONS,
  LANGUAGE_OPTIONS,
  SEARCH_DELAY,
  STATUS_OPTIONS,
} from './conversations-header.constants';
import { useUser } from '@/hooks/use-user/use-user';
import { useScreenSize } from '@/hooks/use-screen-size/use-screen-size';
import Icon from '@ant-design/icons';
import { CustomRangePicker } from '../custom-range-picker/custom-range-picker';
import dayjs, { Dayjs } from 'dayjs';
import { CustomFilters } from '../custom-filters/custom-filters';

interface ConversationsHeaderProps {
  calls: number | string;
  companiesList: string[];
  trendingTopics: string[];
  onRefreshData: () => void;
  onExportHistory: () => void;
  filters: CallLogFilters;
  onChangeFilters: (filters: CallLogFilters) => void;
  dates: [Dayjs | null, Dayjs | null] | null;
  setDates: (dates: [Dayjs | null, Dayjs | null] | null) => void;
  onDateRangeClear: () => void;
  onChangeDateRangeFilter: (filterValue: {
    time_range: { start_time: string; end_time: string };
  }) => void;
  isTrendingTopicsLoading?: boolean;
  searchResults?: string[];
  onSearchQueryChange: (query: string) => void;
  isSearchLoading?: boolean;
  searchError?: Error | null;
  isCompanySelectDisabled?: boolean;
}

export const ConversationsHeader = ({
  calls,
  trendingTopics,
  onRefreshData,
  onExportHistory,
  filters,
  onChangeFilters,
  companiesList,
  dates,
  setDates,
  onDateRangeClear,
  onChangeDateRangeFilter,
  isTrendingTopicsLoading,
  searchResults,
  onSearchQueryChange,
  isSearchLoading,
  searchError,
  isCompanySelectDisabled,
}: ConversationsHeaderProps) => {
  const [filterValue, setFilterValue] = useState(filters.query || '');
  const { userData, isFetched } = useUser();

  const isBayonne = userData?.company?.name.toLocaleLowerCase() === 'Bayonne'.toLowerCase();

  const screenSize = useScreenSize();

  const isMobile = screenSize.width < 768;

  const [activeTopic, setActiveTopic] = useState<string | null>(null);

  const debouncedFilterChange = useMemo(
    () => debounce(onChangeFilters, SEARCH_DELAY),
    [onChangeFilters]
  );

  const handleQueryChange = ({ target: { value } }: React.ChangeEvent<HTMLInputElement>) => {
    setFilterValue(value);
    debouncedFilterChange({ ...filters, query: value, topic: undefined });
    setActiveTopic('');
  };

  const handleConversationTypeChange = (value: string) => {
    const updatedFilters = { ...filters, type: value, topic: undefined };
    if (value !== 'AnalyticsCall') {
      updatedFilters.agent = undefined;
    }
    onChangeFilters(updatedFilters);
    setActiveTopic('');
  };

  const handleStatusChange = (value: string) => {
    onChangeFilters({ ...filters, status: value as 'live' | 'resolved', topic: undefined });
    setActiveTopic('');
  };

  const handleAgentChange = (value: string | undefined) => {
    if (value === undefined) {
      onChangeFilters({ ...filters, agent: undefined, topic: undefined });
    } else {
      onChangeFilters({ ...filters, agent: value === 'human', topic: undefined });
    }
    setActiveTopic('');
  };

  const handleLanguageChange = (value: string | undefined) => {
    onChangeFilters({ ...filters, language: value, topic: undefined });
    setActiveTopic('');
  };

  return (
    <Card className={styles.container} style={{ padding: '16px 24px' }}>
      <Flex vertical>
        <Flex wrap="wrap" gap={20} className={styles.topHeaderContainer}>
          <Flex className={styles.inboundCallsSectionContainer}>
            <Flex gap={30} align="center">
              <Button
                aria-label="Refresh"
                buttonVariant="secondary"
                onClick={onRefreshData}
                icon={<RefreshArrow stroke={styles.iconColor} />}
              ></Button>
              <CallMetricHeader calls={calls} />
            </Flex>

            <Flex className={styles.filtersSectionContainer}>
              {!isBayonne && isFetched && (
                <CallMetricFilters
                  value={{ time_range: filters.time_range }}
                  onChange={(value) => {
                    onChangeFilters({
                      ...filters,
                      time_range: value.time_range || '7d',
                      start_time: undefined,
                      end_time: undefined,
                      topic: undefined,
                    });
                    setDates([null, null]);
                    setActiveTopic('');
                  }}
                />
              )}
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <CustomRangePicker
                  dates={dates}
                  setDates={setDates}
                  onClear={onDateRangeClear}
                  format="YYYY-MM-DD"
                  onChange={({ time_range }) => {
                    onChangeDateRangeFilter({ time_range });
                    onChangeFilters({
                      ...filters,
                      start_time: time_range.start_time,
                      end_time: time_range.end_time,
                      time_range: undefined,
                      topic: undefined,
                    });
                    setActiveTopic('');
                  }}
                  className={
                    filters.start_time !== undefined && filters.end_time !== undefined
                      ? styles.activeDatePicker
                      : ''
                  }
                  value={
                    filters.start_time && filters.end_time
                      ? [dayjs(filters.start_time), dayjs(filters.end_time)]
                      : [undefined, undefined]
                  }
                />
              </div>
            </Flex>
          </Flex>

          <Flex className={styles.dropdownContainer} gap={10} wrap="wrap">
            <Dropdown
              aria-label="conversation type"
              className={styles.dropdown}
              wrapperClassName={styles.wrapperClassName}
              dropdownListClass={styles.durationDropdownList}
              onChange={handleConversationTypeChange}
              placeholder="Conversation type"
              options={CONVERSATION_TYPE_OPTIONS}
              value={filters.type}
            />
            {filters.type === 'AnalyticsCall' && (
              <Dropdown
                aria-label="agent"
                wrapperClassName={styles.wrapperClassName}
                className={styles.dropdown}
                dropdownListClass={styles.agentDropdownList}
                onChange={handleAgentChange}
                placeholder="Agent"
                options={AGENT_OPTIONS}
                value={filters.agent !== undefined ? (filters.agent ? 'human' : 'ai') : undefined}
              />
            )}
            <Dropdown
              aria-label="status"
              wrapperClassName={styles.wrapperClassName}
              className={styles.dropdown}
              dropdownListClass={styles.statusDropdownList}
              onChange={handleStatusChange}
              placeholder="Status"
              options={STATUS_OPTIONS}
              value={filters.status}
            />
            <Dropdown
              aria-label="language"
              wrapperClassName={styles.wrapperClassName}
              className={styles.dropdown}
              dropdownListClass={styles.languageDropdownList}
              onChange={handleLanguageChange}
              placeholder="Language"
              options={LANGUAGE_OPTIONS}
              value={filters.language}
            />
            {isFetched && (
              <CustomFilters
                customClassName={styles.customSelectClassName}
                companiesList={companiesList}
                filters={filters}
                defaultCompanyValue={undefined}
                onChangeFilters={(updatedFilters) => {
                  onChangeFilters({ ...filters, ...updatedFilters, topic: undefined });
                  setActiveTopic('');
                }}
                onSearchQueryChange={onSearchQueryChange}
                searchResults={searchResults}
                isSearchLoading={isSearchLoading}
                searchError={searchError}
                disabled={isCompanySelectDisabled}
              />
            )}
          </Flex>
        </Flex>
        <Flex
          gap={20}
          className={styles.bottomHeaderContainer}
          wrap="wrap"
          justify="space-between"
          align="center"
        >
          <Flex align="center" wrap className={styles.trendingTopicsButton}>
            {trendingTopics.length === 0 && !isTrendingTopicsLoading ? (
              <p className={styles.trendingTopicsEmpty}>
                <Icon
                  component={() => <Glass width="10" height="14" className={styles.glassIcon} />}
                />
                No trending topics to be displayed
              </p>
            ) : (
              <>
                <div className={styles.trendingTopicsLabel}>
                  <span>Trending topics:</span>
                  {isTrendingTopicsLoading && <Spin size="small" style={{ marginLeft: 8 }} />}
                </div>

                {trendingTopics.map((title) => (
                  <TopicChip
                    className={styles.topiChipButton}
                    onClick={() => {
                      if (activeTopic === title) {
                        setActiveTopic('');
                        onChangeFilters({ ...filters, topic: '' });
                      } else {
                        setActiveTopic(title);
                        onChangeFilters({ ...filters, topic: title });
                      }
                    }}
                    isActive={activeTopic === title}
                    key={title}
                    title={title}
                    variant="button"
                  />
                ))}
              </>
            )}
          </Flex>

          <Flex justify="space-between" gap={20} className={styles.searchSectionContainer}>
            <Flex vertical gap={5} className={styles.searchContainer}>
              <Input
                onChange={(event) => handleQueryChange(event)}
                value={filterValue}
                placeholder="Search"
                prefix={
                  <Search stroke={styles.iconColor} height={16} width={16} strokeWidth={2.5} />
                }
              />
              <Flex gap={5}>
                <Button
                  rightIcon
                  onClick={onExportHistory}
                  icon={<Download1 stroke={styles.iconColor} style={{ width: 10, height: 10 }} />}
                  style={{
                    fontSize: 10,
                    fontWeight: 400,
                    color: styles.buttonColor,
                    padding: isMobile ? '0px 5px' : '0px 12px',
                  }}
                  buttonVariant="tertiary"
                >
                  <Tooltip title="Prepare and download the call history for a selected period.">
                    Export
                  </Tooltip>
                </Button>
              </Flex>
            </Flex>
          </Flex>
        </Flex>
      </Flex>
    </Card>
  );
};
